import { useCallback, useMemo, useRef, useState } from 'react'
import type { SessionCommand } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { IconGlyph } from '../../icons/registry'
import { optionOf, sortedOptions } from '../../lib/attributes'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import type { StepProps } from '../types'
import { usePendingExit } from '../usePendingExit'

/**
 * Reordering, with drag as an ENHANCEMENT rather than the mechanism.
 *
 * WCAG 2.2 SC 2.5.7 (Dragging Movements) requires a single-pointer, non-drag
 * alternative for anything that can be dragged. Every row therefore carries
 * real move-up / move-down buttons that are in the tab order, operate on
 * click or Enter/Space like any button, keep focus on the control that was
 * pressed so a second press repeats the move, and announce the new position
 * through a polite live region for screen reader users. Pointer drag is layered
 * on top for people who want it and is never the only route.
 */
export function PreferenceOrderStep({ step, ctx }: StepProps<'preference_order'>) {
  const { view, locale } = ctx
  const t = uiStrings(locale)

  const definition = view.tenant.attributes.find((a) => a.key === step.attributeKey)
  const seed = view.preferenceSeeds[step.attributeKey]

  const recommended = useMemo(() => new Set(seed?.recommended ?? []), [seed])

  const initial = useMemo(() => {
    const saved = view.session.answers.preferenceOrders[step.attributeKey]
    const fromDefinition = definition ? sortedOptions(definition).map((o) => o.value) : []
    const base = saved ?? seed?.order ?? fromDefinition
    // Anything the seed did not mention still belongs on the list unless the
    // step says otherwise — the customer must be able to promote it.
    const merged = step.includeUnrecommended ? [...base, ...fromDefinition.filter((v) => !base.includes(v))] : base
    return merged.filter((value, index) => merged.indexOf(value) === index)
  }, [view.session.answers.preferenceOrders, step.attributeKey, step.includeUnrecommended, seed, definition])

  const [order, setOrder] = useState<readonly string[]>(initial)
  /**
   * The list opens in the SEED's order when she has said nothing, and a seed
   * is a recommendation rather than her ranking. Only a move she made turns
   * what is on screen into an answer worth carrying out of it.
   */
  const [reordered, setReordered] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const buttonRefs = useRef(new Map<string, HTMLButtonElement>())

  const labelFor = useCallback(
    (value: string) => {
      const option = optionOf(definition, value)
      return option ? text(option.label, locale) : value
    },
    [definition, locale],
  )

  const move = useCallback(
    (from: number, to: number, focusKey?: string) => {
      if (to < 0 || to >= order.length || from === to) return
      const next = [...order]
      const [moved] = next.splice(from, 1)
      if (moved === undefined) return
      next.splice(to, 0, moved)
      setReordered(true)
      setOrder(next)
      setAnnouncement(t.positionAnnouncement(labelFor(moved), to + 1, next.length))
      if (focusKey) {
        // Keep focus on the control that was pressed so a second press repeats.
        window.requestAnimationFrame(() => buttonRefs.current.get(focusKey)?.focus())
      }
    },
    [order, labelFor, t],
  )

  const command: SessionCommand = { type: 'reorder', attributeKey: step.attributeKey, order: [...order] }

  usePendingExit(ctx, reordered ? () => [command] : null)

  return (
    <StepShell
      progress={ctx.progress}
      locale={locale}
      skip={step.skippable ? { label: t.skipThis, onSkip: () => ctx.advance(), disabled: ctx.pending } : undefined}
      title={text(step.title, locale) || (definition ? text(definition.label, locale) : step.attributeKey)}
      body={text(step.body, locale)}
      footer={
        <div className="pb-2" style={{ background: 'linear-gradient(to top, var(--fr-ground-deep) 62%, transparent)' }}>
          <button
            type="button"
            className="fr-btn fr-btn-primary w-full"
            disabled={ctx.pending}
            onClick={() => ctx.advance([command])}
          >
            {t.continue}
          </button>

        </div>
      }
    >
      <p className="fr-eyebrow mb-4">{t.reorderHint}</p>

      <ol className="space-y-2">
        {order.map((value, index) => {
          const option = optionOf(definition, value)
          const label = labelFor(value)
          const upKey = `${value}:up`
          const downKey = `${value}:down`
          return (
            <li
              key={value}
              className="fr-row"
              draggable
              data-dragging={dragIndex === index}
              data-dropbefore={dropIndex === index && dragIndex !== null && dragIndex !== index}
              onDragStart={(event) => {
                setDragIndex(index)
                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.setData('text/plain', value)
              }}
              onDragOver={(event) => {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                setDropIndex(index)
              }}
              onDrop={(event) => {
                event.preventDefault()
                if (dragIndex !== null) move(dragIndex, index)
                setDragIndex(null)
                setDropIndex(null)
              }}
              onDragEnd={() => {
                setDragIndex(null)
                setDropIndex(null)
              }}
            >
              <span className="fr-display w-6 shrink-0 text-[1.25rem] tabular-nums" style={{ color: 'var(--fr-primary)' }}>
                {index + 1}
              </span>

              <IconGlyph name={option?.icon} label={label} size={30} className="shrink-0 opacity-80" />

              <span className="min-w-0 flex-1">
                <span className="fr-display block truncate text-[1.125rem]">{label}</span>
                {recommended.has(value) ? (
                  <span className="fr-eyebrow" style={{ color: 'var(--fr-primary)' }}>
                    {t.suggested}
                  </span>
                ) : null}
              </span>

              <span className="flex shrink-0 gap-1">
                <button
                  type="button"
                  className="fr-iconbtn"
                  ref={(node) => {
                    if (node) buttonRefs.current.set(upKey, node)
                    else buttonRefs.current.delete(upKey)
                  }}
                  disabled={index === 0}
                  aria-label={`${t.moveUp}: ${label}`}
                  onClick={() => move(index, index - 1, upKey)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <path d="M12 19V6M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="fr-iconbtn"
                  ref={(node) => {
                    if (node) buttonRefs.current.set(downKey, node)
                    else buttonRefs.current.delete(downKey)
                  }}
                  disabled={index === order.length - 1}
                  aria-label={`${t.moveDown}: ${label}`}
                  onClick={() => move(index, index + 1, downKey)}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <path d="M12 5v13M19 12l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </span>
            </li>
          )
        })}
      </ol>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </StepShell>
  )
}
